// SEO helper utilities for the public marketing site — Phase 3 Module 12.
//
// Keep URL building in one place so page components stay declarative.

const API = process.env.REACT_APP_BACKEND_URL;

function siteOrigin() {
  // Prefer the window origin (handles preview / prod / dev identically).
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  // Fall back to the backend URL (same host in our deployment model).
  return API || "";
}

/**
 * Absolute canonical URL for a public-site page.
 *
 *   siteUrl('little-oak')                    → https://.../site/little-oak
 *   siteUrl('little-oak', 'announcements')   → https://.../site/little-oak/announcements
 *   siteUrl('little-oak', 'page/about')      → https://.../site/little-oak/page/about
 */
export function siteUrl(orgSlug, path = "") {
  const base = `${siteOrigin()}/site/${encodeURIComponent(orgSlug)}`;
  if (!path) return base;
  return `${base}/${path}`.replace(/([^:])\/{2,}/g, "$1/");
}

/**
 * OG image URL for a public-site page. Built against the backend because
 * the image is rendered server-side (social crawlers don't run JS).
 *
 *   siteOgImage('little-oak', 'home')
 *   siteOgImage('little-oak', 'announcements')
 *   siteOgImage('little-oak', 'page', 'About Little Oak')
 */
export function siteOgImage(orgSlug, type = "home", title = null) {
  if (!API) return null;
  const params = new URLSearchParams({ type });
  if (title) params.set("title", String(title).slice(0, 140));
  return `${API}/api/og/${encodeURIComponent(orgSlug)}.png?${params.toString()}`;
}

/**
 * JSON-LD (Schema.org) for the org's home page. Helps Google and other
 * rich-result crawlers tag this as a local business / childcare center.
 */
export function jsonLdForOrg(org, canonical) {
  if (!org) return null;
  return {
    "@context":   "https://schema.org",
    "@type":      "ChildCare",
    name:         org.display_name || org.name,
    url:          canonical,
    image:        org.logo_url || undefined,
    email:        org.contact_email || undefined,
    telephone:    org.contact_phone || undefined,
    address:      org.contact_address
      ? { "@type": "PostalAddress", streetAddress: org.contact_address }
      : undefined,
  };
}

/**
 * BreadcrumbList JSON-LD for any public-site route. Builds a compact
 * two-or-three-hop trail from the org home → optional intermediate
 * segment → current page. Google's rich-results validator is happy
 * with relative position + absolute URLs.
 */
export function jsonLdBreadcrumb({ orgSlug, orgName, trail }) {
  // `trail` is [{ name, href }]; we prepend home automatically.
  if (!orgSlug || !Array.isArray(trail)) return null;
  const items = [
    { name: orgName || "Home", href: siteUrl(orgSlug) },
    ...trail,
  ].map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: it.href,
  }));
  return {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: items,
  };
}

/**
 * Article JSON-LD for a CMS page. Combined with BreadcrumbList via a
 * Schema.org @graph so Google can ingest both in a single <script>.
 */
export function jsonLdForCmsPage({ org, page, orgSlug }) {
  if (!org || !page || !orgSlug) return null;
  const canonical = siteUrl(orgSlug, `page/${page.slug}`);
  const orgName = org.display_name || org.name;

  // Module E · auto-detect specialised structured-data types so Google
  // can render rich snippets. The detection is lossless: we still emit
  // Article + BreadcrumbList for every page, then add FAQPage / Service
  // when the slug + body match.
  const slug  = (page.slug || "").toLowerCase();
  const title = (page.title || "").toLowerCase();
  const isFaq = slug.startsWith("faq")
    || /faq|frequently asked/i.test(title);
  const isPricing = /pricing|tuition|cost|fees?/.test(slug)
    || /pricing|tuition|cost|fees?/.test(title);

  const graph = [
    {
      "@type":         "Article",
      headline:        page.title,
      description:     metaDescription(page.body_md, 160) || undefined,
      mainEntityOfPage: canonical,
      url:             canonical,
      datePublished:   page.published_at || page.created_at || undefined,
      dateModified:    page.updated_at || page.published_at || undefined,
      publisher: {
        "@type": "Organization",
        name:    orgName,
        logo: org.logo_url
          ? { "@type": "ImageObject", url: org.logo_url }
          : undefined,
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: orgName || "Home", item: siteUrl(orgSlug) },
        { "@type": "ListItem", position: 2, name: page.title,        item: canonical },
      ],
    },
  ];

  if (isFaq) {
    const qa = parseFaqQuestions(page.body_md);
    if (qa.length > 0) {
      graph.push({
        "@type":      "FAQPage",
        mainEntity:   qa.map((p) => ({
          "@type": "Question",
          name:    p.q,
          acceptedAnswer: {
            "@type": "Answer",
            text:    p.a,
          },
        })),
      });
    }
  } else if (isPricing) {
    graph.push({
      "@type":       "Service",
      name:          page.title,
      provider:      { "@type": "Organization", name: orgName },
      areaServed:    org.contact_address || undefined,
      serviceType:   "Childcare tuition",
      url:           canonical,
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph":   graph,
  };
}

/** Parse `## Question?` blocks out of a markdown body. The answer is
 *  every paragraph up to the next `##` (or end-of-body). Returns an
 *  array of `{q, a}` pairs — empty if none detected. */
function parseFaqQuestions(md) {
  if (!md) return [];
  const lines = String(md).replace(/\\n/g, "\n").split(/\r?\n/);
  const out = [];
  let cur = null;
  for (const raw of lines) {
    const line = raw.trim();
    const heading = line.match(/^##\s+(.+?)\s*$/);
    if (heading) {
      if (cur && cur.a.trim()) out.push({ q: cur.q, a: cur.a.trim() });
      cur = { q: heading[1].replace(/[#*_`]/g, "").trim(), a: "" };
      continue;
    }
    if (cur && line) cur.a += (cur.a ? " " : "") + line;
  }
  if (cur && cur.a.trim()) out.push({ q: cur.q, a: cur.a.trim() });
  // Trim each answer to a sensible length to keep the JSON-LD compact.
  return out
    .filter((p) => p.q && p.a)
    .map((p) => ({ q: p.q, a: p.a.replace(/\s+/g, " ").slice(0, 600) }));
}

/**
 * Truncate arbitrary text to a meta-description-friendly length.
 * Strips markdown tokens and collapses whitespace so seeded `\n` chars
 * and stray `#` headings don't bleed into the final string.
 */
export function metaDescription(text, max = 160) {
  if (!text) return "";
  return String(text)
    .replace(/\\n/g, " ")
    .replace(/[#*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}
